/**
 * Error message formatting and actionable error utilities
 */

import toast from 'react-hot-toast';

/**
 * Get user-friendly error message from error object
 * @param {Error|string} error - Error object or message
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
  const errorMessage = typeof error === 'string' ? error : error?.message || 'An unexpected error occurred';
  const lowerMessage = errorMessage.toLowerCase();
  
  // Network errors
  if (lowerMessage.includes('network') || 
      lowerMessage.includes('fetch') || 
      lowerMessage.includes('connection') ||
      lowerMessage.includes('timeout') ||
      lowerMessage.includes('failed to fetch')) {
    return 'Check your internet connection and try again.';
  }
  
  // Permission errors
  if (lowerMessage.includes('permission') || 
      lowerMessage.includes('unauthorized') ||
      lowerMessage.includes('forbidden') ||
      lowerMessage.includes('access denied') ||
      lowerMessage.includes('row-level security')) {
    return 'You may not have permission to perform this action. Contact support if you believe this is an error.';
  }
  
  // Validation errors
  if (lowerMessage.includes('validation') || 
      lowerMessage.includes('invalid') ||
      lowerMessage.includes('required') ||
      lowerMessage.includes('format')) {
    return errorMessage; // Return original validation message
  }
  
  // Rate limiting
  if (lowerMessage.includes('rate limit') || 
      lowerMessage.includes('too many')) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  
  // Generic fallback
  return 'Please try again. If the problem persists, contact support.';
};

/**
 * Show actionable error toast with retry button
 * @param {Error|string} error - Error object or message
 * @param {Function} retryAction - Function to call on retry
 * @param {string} context - Context of the error (e.g., "saving profile", "approving submission")
 */
export const showActionableError = (error, retryAction = null, context = '') => {
  const message = getErrorMessage(error);
  const contextText = context ? `${context}: ` : '';
  
  toast.error(
    (t) => (
      <div>
        <div style={{ marginBottom: retryAction ? '8px' : '0' }}>
          {contextText}{message}
        </div>
        {retryAction && (
          <button
            onClick={() => {
              toast.dismiss(t.id);
              retryAction();
            }}
            style={{
              background: 'transparent',
              border: '1px solid currentColor',
              color: 'inherit',
              padding: '4px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              marginTop: '8px',
            }}
          >
            Retry
          </button>
        )}
      </div>
    ),
    {
      duration: retryAction ? 10000 : 5000,
    }
  );
};

/**
 * Get actionable error component (for use in JSX)
 * @param {Error|string} error - Error object or message
 * @param {Function} retryAction - Function to call on retry
 * @param {string} context - Context of the error
 * @returns {Object} Toast component configuration
 */
export const getActionableError = (error, retryAction = null, context = '') => {
  const message = getErrorMessage(error);
  const contextText = context ? `${context}: ` : '';
  
  return {
    toast: (t) => (
      <div>
        <div style={{ marginBottom: retryAction ? '8px' : '0' }}>
          {contextText}{message}
        </div>
        {retryAction && (
          <button
            onClick={() => {
              toast.dismiss(t.id);
              retryAction();
            }}
            style={{
              background: 'transparent',
              border: '1px solid currentColor',
              color: 'inherit',
              padding: '4px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              marginTop: '8px',
            }}
          >
            Retry
          </button>
        )}
      </div>
    ),
    options: {
      duration: retryAction ? 10000 : 5000,
    }
  };
};

